import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, Printer } from 'lucide-react';
import { formatCATDateTime } from '../../utils/timezone';
import { downloadDailySummaryCSV, printDailySummary } from './exportUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DailySummaryModal({ open, onOpenChange, canViewAllData, isSuperAdmin, hospitalName, hospitalStats, authCfg }) {
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [hospital, setHospital] = useState('ALL');
  const [claims, setClaims] = useState([]);
  const totalUsd = useMemo(() => claims.reduce((sum, c) => sum + Number(c.total_claim_amount || 0), 0), [claims]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (d, hosp) => {
    setLoading(true);
    setError('');
    try {
      if (!d) {
        setClaims([]);
        return;
      }
      const params = { date: d, limit: 100000, skip: 0 };
      if (isSuperAdmin && hosp && hosp !== 'ALL') params.hospital_name = hosp;
      const res = await axios.get(`${API}/claims/daily-summary`, { ...authCfg, params });
      setClaims(res.data?.claims || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load daily claims summary');
      setClaims([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load(date, hospital);
    // eslint-disable-next-line
  }, [open, date, hospital]);

  const hospLabelForCsv = isSuperAdmin ? (hospital || 'ALL') : (hospitalName || 'hospital');
  const hospLabelForPrint = isSuperAdmin ? (hospital === 'ALL' ? 'All Hospitals' : hospital) : hospitalName;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-700" />
            Daily Claims Summary
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-end">
            <div>
              <Label className="text-sm">Select Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="daily-summary-date" />
            </div>

            {canViewAllData && (
              <div>
                <Label className="text-sm">Hospital Filter</Label>
                <Select value={hospital} onValueChange={setHospital}>
                  <SelectTrigger className="mt-1 w-[260px]"><SelectValue placeholder="All Hospitals" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Hospitals</SelectItem>
                    {Object.keys(hospitalStats || {}).map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex gap-2 md:ml-auto">
              <Button onClick={() => printDailySummary(claims, date, hospLabelForPrint)} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button variant="outline" onClick={() => downloadDailySummaryCSV(claims, date, hospLabelForCsv)} disabled={loading}>
                Download CSV
              </Button>
            </div>
          </div>

          {error && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

          {loading ? (
            <div className="text-center py-8 text-gray-600">Loading daily summary...</div>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">#</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Claim ID</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Patient</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Time</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Serial No</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Amount (USD)</th>
                    <th className="text-center p-3 text-sm font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="p-4 text-center text-gray-500">No claims found for this date.</td>
                    </tr>
                  ) : (
                    claims.map((c, idx) => (
                      <tr key={`${c.claim_id}-${idx}`} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm text-gray-700">{idx + 1}</td>
                        <td className="p-3 text-sm font-medium text-gray-800">{c.claim_id}</td>
                        <td className="p-3 text-sm text-gray-700">{c.patient_name}</td>
                        <td className="p-3 text-sm text-gray-600">{c.timestamp ? formatCATDateTime(c.timestamp) : ''}</td>
                        <td className="p-3 text-sm text-gray-700">{c.patient_serial_number}</td>
                        <td className="p-3 text-sm text-gray-800 text-right font-medium">{Number(c.total_claim_amount || 0).toFixed(2)}</td>
                        <td className="p-3 text-center">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                            c.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800'
                              : c.status === 'PAID' ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {c.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {claims.length > 0 && (
                <div className="bg-gray-50 border-t p-3 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Total Claims: {claims.length}</span>
                  <span className="text-sm font-bold text-green-700">
                    Total Amount: ${totalUsd.toFixed(2)} USD
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
